<template>
  <div class="checkout-modal-overlay" @click.self="$emit('close')">
    <div class="checkout-container">
      <div class="checkout-header">
        <h2><i class="fas fa-shopping-cart"></i> Complete Your Purchase</h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="checkout-content">
        <!-- Course Summary -->
        <div class="course-summary">
          <div v-if="course.thumbnail" class="course-thumbnail">
            <img :src="course.thumbnail" :alt="course.title" />
          </div>
          <div class="course-info">
            <h3>{{ course.title }}</h3>
            <p class="course-description">{{ course.short_description || course.description }}</p>
            
            <div class="course-meta">
              <span v-if="course.estimated_duration_minutes" class="meta-item">
                <i class="fas fa-clock"></i>
                {{ formatDuration(course.estimated_duration_minutes) }}
              </span>
              <span v-if="course.certification_type" class="meta-item">
                <i class="fas fa-certificate"></i>
                {{ course.certification_type }}
              </span>
              <span v-if="course.instructor_name" class="meta-item">
                <i class="fas fa-user"></i>
                {{ course.instructor_name }}
              </span>
            </div>
          </div>
        </div>

        <!-- Annual Free Course Banner -->
        <div v-if="freeCourseAvailable" class="free-course-banner">
          <i class="fas fa-gift"></i>
          <div>
            <strong>Your free course benefit is available!</strong>
            <p>As a SCAV member, you get one free course per year. This course will be enrolled at no charge.</p>
          </div>
        </div>

        <!-- Price Breakdown -->
        <div class="price-breakdown">
          <div class="price-row">
            <span class="label">Course Price:</span>
            <span class="price">${{ formatPrice(course.price) }}</span>
          </div>

          <!-- Free Course Row -->
          <div v-if="freeCourseAvailable" class="price-row discount-row free-row">
            <span class="label discount-label">
              <i class="fas fa-gift"></i> Annual member free course
            </span>
            <span class="discount-value">-${{ formatPrice(course.price) }}</span>
          </div>
          
          <!-- Member Discount Row (only when free benefit not in play) -->
          <div v-else-if="pricingInfo.isMember" class="price-row discount-row">
            <span class="label discount-label">
              <i class="fas fa-tag"></i> Membership discount
            </span>
            <span class="discount-value">{{ pricingInfo.discountPercentage }}%</span>
          </div>
          
          <div class="price-row total">
            <span class="label">Total:</span>
            <span v-if="freeCourseAvailable" class="price free-price">FREE</span>
            <span v-else class="price" :class="{ 'discounted': pricingInfo.isMember }">
              ${{ formatPrice(pricingInfo.finalPrice) }}
            </span>
          </div>
        </div>

        <!-- Non-member upsell (only for non-members) -->
        <div v-if="!pricingInfo.isMember" class="member-upsell">
          <i class="fas fa-info-circle"></i>
          <span>SCAV members receive <strong>40% off</strong> all courses and one free course per year!
            <a href="https://scav.org/membership" target="_blank" rel="noopener">Learn more</a>
          </span>
        </div>

        <!-- What's Included -->
        <div class="whats-included">
          <h4>What's Included</h4>
          <ul>
            <li><i class="fas fa-check"></i> Lifetime access to course content</li>
            <li><i class="fas fa-check"></i> All {{ course.video_count || 'course' }} videos and materials</li>
            <li v-if="course.certification_type"><i class="fas fa-check"></i> {{ course.certification_type }} upon completion</li>
            <li><i class="fas fa-check"></i> Progress tracking and certificates</li>
            <li><i class="fas fa-check"></i> Access on desktop and mobile</li>
          </ul>
        </div>

        <!-- Checkout Button -->
        <button 
          @click="proceedToCheckout" 
          class="btn-checkout"
          :class="{ 'btn-checkout-free': freeCourseAvailable }"
          :disabled="loading"
        >
          <i :class="freeCourseAvailable ? 'fas fa-gift' : 'fas fa-lock'"></i>
          {{ loading ? 'Processing...' : freeCourseAvailable ? 'Enroll for Free' : 'Proceed to Secure Checkout' }}
        </button>

        <!-- Security Notice -->
        <div v-if="!freeCourseAvailable" class="security-notice">
          <i class="fas fa-shield-alt"></i>
          Secure payment processing powered by Stripe. Your payment information is encrypted and secure.
        </div>

        <!-- Refund Policy - Removed per client request -->
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import auth from '@/services/auth';

export default {
  name: 'CourseCheckout',

  props: {
    course: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    /**
     * Calculate pricing info including member discount
     * @returns {Object} { originalPrice, discountPercentage, discountAmount, finalPrice, isMember }
     */
    pricingInfo() {
      return auth.calculateMemberPrice(parseFloat(this.course.price) || 0);
    },

    /**
     * True if the member has their annual free course benefit available.
     * When true the checkout bypasses Stripe entirely.
     * @returns {boolean}
     */
    freeCourseAvailable() {
      return auth.hasAnnualFreeCourseAvailable();
    },
  },

  methods: {
    async proceedToCheckout() {
      this.loading = true;
      try {
        const currentUrl = window.location.origin;
        // Note: Backend calculates the discount server-side for security
        // Frontend display is for UX only
        const response = await api.post(`/lms/courses/${this.course.id}/create-checkout-session/`, {
          success_url: `${currentUrl}/learning/courses/${this.course.id}?enrollment=success`,
          cancel_url: `${currentUrl}/learning/courses/${this.course.id}`,
        });

        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        this.$toast?.error(error.response?.data?.error || 'Failed to create checkout session. Please try again.');
        this.loading = false;
      }
    },

    /**
     * Format price to 2 decimal places
     * @param {number|string} price 
     * @returns {string}
     */
    formatPrice(price) {
      return parseFloat(price).toFixed(2);
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      }
      return `${mins} minutes`;
    },
  },
};
</script>

<style scoped>
.checkout-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  overflow-y: auto;
}

.checkout-container {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.checkout-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.checkout-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checkout-header h2 i {
  color: var(--primary-color, #4f46e5);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #1a1a1a;
}

.checkout-content {
  padding: 2rem;
  overflow-y: auto;
}

.course-summary {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
}

.course-thumbnail {
  flex-shrink: 0;
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
}

.course-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.course-info {
  flex: 1;
}

.course-info h3 {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary, #1a1a1a);
}

.course-description {
  font-size: 0.875rem;
  color: var(--text-secondary, #6b7280);
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.meta-item {
  font-size: 0.75rem;
  color: var(--text-secondary, #6b7280);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.meta-item i {
  color: var(--primary-color, #4f46e5);
}

.price-breakdown {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.price-row.total {
  border-top: 2px solid #e5e7eb;
  margin-top: 0.5rem;
  padding-top: 1rem;
  font-weight: 700;
  font-size: 1.125rem;
}

.price-row .label {
  color: var(--text-secondary, #6b7280);
}

.price-row .price {
  color: var(--primary-color, #4f46e5);
  font-weight: 700;
  font-size: 1.25rem;
}

.whats-included {
  margin-bottom: 2rem;
}

.whats-included h4 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: var(--text-primary, #1a1a1a);
}

.whats-included ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.whats-included li {
  padding: 0.5rem 0;
  color: var(--text-secondary, #6b7280);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.whats-included li i {
  color: #10b981;
  font-size: 0.875rem;
}

.btn-checkout {
  width: 100%;
  padding: 1.25rem;
  background: var(--primary-color, #4f46e5);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.btn-checkout:hover:not(:disabled) {
  background: #4338ca;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(79, 70, 229, 0.3);
}

.btn-checkout:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-checkout i {
  font-size: 1rem;
}

.security-notice {
  text-align: center;
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 8px;
  color: #166534;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.security-notice i {
  color: #10b981;
  font-size: 1rem;
}

.refund-policy {
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-secondary, #6b7280);
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
}

.refund-policy strong {
  color: var(--text-primary, #1a1a1a);
}

/* Annual Free Course Banner */
.free-course-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1.5px solid #86efac;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  color: #14532d;
}

.free-course-banner i {
  color: #16a34a;
  font-size: 1.25rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.free-course-banner strong {
  display: block;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.free-course-banner p {
  margin: 0;
  font-size: 0.8125rem;
  color: #166534;
  line-height: 1.4;
}

/* Free price label */
.price.free-price {
  color: #16a34a !important;
  font-size: 1.375rem;
  letter-spacing: 0.03em;
}

/* Green checkout button for free enrollments */
.btn-checkout.btn-checkout-free {
  background: #16a34a;
}

.btn-checkout.btn-checkout-free:hover:not(:disabled) {
  background: #15803d;
  box-shadow: 0 8px 16px rgba(22, 163, 74, 0.3);
}

/* Member Discount Styling */
.discount-row {
  background: linear-gradient(90deg, #fef3c7 0%, #fde68a 100%);
  margin: 0.5rem -1.5rem;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0;
}

.discount-label {
  color: #92400e !important;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.discount-label i {
  color: #d97706;
}

.discount-value {
  color: #92400e;
  font-weight: 700;
  font-size: 1.125rem;
}

.price.discounted {
  color: #059669 !important;
}

.member-upsell {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #1e40af;
}

.member-upsell i {
  color: #3b82f6;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.member-upsell a {
  color: #2563eb;
  text-decoration: underline;
}

.member-upsell a:hover {
  color: #1d4ed8;
}

@media (max-width: 768px) {
  .checkout-container {
    max-height: 95vh;
  }

  .checkout-header {
    padding: 1rem 1.5rem;
  }

  .checkout-header h2 {
    font-size: 1.25rem;
  }

  .checkout-content {
    padding: 1.5rem;
  }

  .course-summary {
    flex-direction: column;
  }

  .course-thumbnail {
    width: 100%;
    height: 180px;
  }

  .course-meta {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>

